import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Row, Col, Badge, InputGroup, Spinner, Alert, Card, Tabs, Tab } from 'react-bootstrap';
import { FaSearch, FaCheck, FaTimes, FaClock, FaEye, FaUsers, FaBuilding, FaChartBar } from 'react-icons/fa';
import api from '../../../api/apiClient';
import { toast } from 'react-toastify';

const ManageLicenses = () => {
  // State for license requests
  const [licenseRequests, setLicenseRequests] = useState([]);
  const [licenseUsage, setLicenseUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('requests');
  
  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Form states
  const [approvedLicenses, setApprovedLicenses] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [notes, setNotes] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // Status configurations
  const STATUS_CONFIG = {
    pending: { variant: 'warning', icon: FaClock, label: 'Pending' },
    approved: { variant: 'success', icon: FaCheck, label: 'Approved' },
    rejected: { variant: 'danger', icon: FaTimes, label: 'Rejected' }
  };

  // Fetch license requests
  const fetchLicenseRequests = async () => {
    try {
      setLoading(true);
      const response = await api.licenses.getAllRequests(filterStatus);
      if (response.data.success) {
        setLicenseRequests(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching license requests:', error);
      toast.error('Failed to load license requests: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch license usage
  const fetchLicenseUsage = async () => {
    try {
      setUsageLoading(true);
      const response = await api.licenses.getAllUsage();
      if (response.data.success) {
        setLicenseUsage(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching license usage:', error);
      toast.error('Failed to load license usage: ' + (error.response?.data?.message || error.message));
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenseRequests();
  }, [filterStatus]);

  useEffect(() => {
    if (activeTab === 'usage') {
      fetchLicenseUsage();
    }
  }, [activeTab]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
  };

  const handleShowApproveModal = (request) => {
    setSelectedRequest(request);
    setApprovedLicenses(request.requested_licenses.toString());
    setNotes(request.notes || '');
    setFormErrors({});
    setShowApproveModal(true);
  };

  const handleShowRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setNotes(request.notes || '');
    setFormErrors({});
    setShowRejectModal(true);
  };


  const handleShowDetailsModal = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleCloseModals = () => {
    setShowApproveModal(false);
    setShowRejectModal(false);
    setShowDetailsModal(false);
    setSelectedRequest(null);
    setApprovedLicenses('');
    setRejectionReason('');
    setNotes('');
    setFormErrors({});
  };

  const validateApprovalForm = () => {
    const errors = {};
    
    if (!approvedLicenses || approvedLicenses <= 0) {
      errors.approvedLicenses = 'Approved licenses must be a positive number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateRejectionForm = () => {
    const errors = {};
    
    if (!rejectionReason.trim()) {
      errors.rejectionReason = 'Rejection reason is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleApproveRequest = async () => {
    if (!validateApprovalForm()) return;

    setModalLoading(true);
    try {
      const response = await api.licenses.approveRequest(
        selectedRequest.id,
        parseInt(approvedLicenses),
        notes.trim() || null
      );
      
      toast.success('License request approved successfully!');
      
      // Show notification if available
      if (response.data.notification) {
        toast.info(`Notification sent: ${response.data.notification.message}`);
      }
      
      handleCloseModals();
      fetchLicenseRequests();
      if (activeTab === 'usage') {
        fetchLicenseUsage();
      }
    } catch (error) {
      console.error('Error approving license request:', error);
      toast.error('Failed to approve license request: ' + (error.response?.data?.message || error.message));
    } finally {
      setModalLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!validateRejectionForm()) return;

    setModalLoading(true);
    try {
      const response = await api.licenses.rejectRequest(
        selectedRequest.id,
        rejectionReason.trim(),
        notes.trim() || null
      );
      
      toast.success('License request rejected successfully!');
      
      // Show notification if available
      if (response.data.notification) {
        toast.info(`Notification sent: ${response.data.notification.message}`);
      }
      
      handleCloseModals();
      fetchLicenseRequests();
    } catch (error) {
      console.error('Error rejecting license request:', error);
      toast.error('Failed to reject license request: ' + (error.response?.data?.message || error.message));
    } finally {
      setModalLoading(false);
    }
  };

  // Filter requests based on search term
  const filteredRequests = licenseRequests.filter(request => {
    const matchesSearch = 
      request.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.requester?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.requester?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.requester?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.id.toString().includes(searchTerm);
    
    return matchesSearch;
  });

  // Filter usage based on search term
  const filteredUsage = licenseUsage.filter(usage => {
    const matchesSearch = 
      usage.organization?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      usage.organization?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-3">

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold mb-0 text-xl text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage Licenses
        </h5>
      </div>
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-6 border-b-4 border-purple-300 rounded-t-2xl overflow-hidden shadow-2xl"
        fill
      >
        <Tab eventKey="requests" title={
          <span className="d-flex align-items-center gap-4 text-1xl font-bold text-purple-700 py-2 px-6 bg-white/50 backdrop-blur">
            <FaClock className="text-yellow-600 text-1xl" />
            License Requests
          </span>
        }>
          <div className="pt-6 md:pt-8"></div>

          {/* Search and Filter Controls */}
          <Row className="g-4 mb-3">
            <Col md={6} className="mb-3 mb-md-0">
              <InputGroup className="shadow-2xl rounded-xl overflow-hidden border-0 border-purple-300">
                <InputGroup.Text className="bg-white border-0 text-purple-600 px-6 text-1xl">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by request ID, organization, or requester..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="border-0 focus:ring-0 focus:outline-none text-xl py-2"
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select 
                value={filterStatus} 
                onChange={handleFilterChange}
                className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </Form.Select>
            </Col>
          </Row>

          {/* License Requests Table */}
          {loading ? (
            <div className="text-center py-32">
              <Spinner animation="border" className="text-purple-600 w-24 h-24" />
              <p className="mt-8 text-1xl font-extrabold text-purple-700">Loading Requests...</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl shadow-2xl bg-white -mx-4 md:mx-0">
    <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl shadow-lg bg-white">
           <Table bordered hover className="m-0 min-w-max text-xs sm:text-sm">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3 font-bold text-left">Request ID</th>
                    <th className="py-2 px-3 font-bold text-left">Organization</th>
                    <th className="py-2 px-3 font-bold text-left">Requested By</th>
                    <th className="py-2 px-3 font-bold text-left">Licenses Requested</th>
                    <th className="py-2 px-3 font-bold text-left">Request Date</th>
                    <th className="py-2 px-3 font-bold text-left">Status</th>
                    <th className="py-2 px-3 font-bold text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-32">
                        <Alert variant="info" className="bg-purple-50 border-4 border-purple-300 shadow-2xl mx-20 py-16">
                          <p className="text-5xl font-extrabold text-purple-700 mb-2">No license requests found</p>
                          <p className="text-1xl text-purple-600">{searchTerm && 'Try adjusting your search terms.'}</p>
                        </Alert>
                      </td>
                    </tr>
                  ) : (
                    filteredRequests.map((request) => {
                      const statusConfig = STATUS_CONFIG[request.status];
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <tr key={request.id} className="hover:bg-purple-50 transition-all duration-300">
                          <td className="py-2 px-4 align-middle">
                            <strong className="text-1xl font-bold text-purple-700">#{request.id}</strong>
                          </td>
                          <td className="py-2 px-4 align-middle">
                            <div>
                              <strong className="block text-gray-800">{request.organization?.name}</strong>
                              {request.organization?.email && (
                                <div className="text-gray-600 text-sm mt-1">{request.organization.email}</div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-4 align-middle">
                            <div>
                              <strong className="block text-gray-800">{request.requester?.first_name} {request.requester?.last_name}</strong>
                              <div className="text-gray-600 text-sm mt-1">{request.requester?.email}</div>
                            </div>
                          </td>
                          <td className="py-2 px-3 text-left align-middle">
                            <div className="text-1xl font-extrabold text-purple-700">{request.requested_licenses}</div>
                            {request.status === 'approved' && (
                              <div className="text-emerald-600 font-bold mt-2">
                                Approved: {request.approved_licenses}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-left align-middle text-gray-700 break-all">
                                                          {formatDate(request.createdAt)}
                          </td>
                          <td className="py-2 px-3 text-left align-middle">
                            <Badge bg={statusConfig.variant} className="px-8 py-2 text-xl font-bold shadow-lg">
                              {/* <StatusIcon className="me-3 text-1xl" /> */}
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-left align-middle">
                            <div className="d-flex gap-4 justify-content-left">
                              <Button
                                            bsPrefix="custom-hamburger"

                                onClick={() => handleShowDetailsModal(request)}
                                title="View Details"
      className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded p-1"
                              >
                                <FaEye className="w-5 h-5 text-blue-600" />
                              </Button>
                              {request.status === 'pending' && (
                                <>
                                  <Button
                                                bsPrefix="custom-hamburger"

                                    onClick={() => handleShowApproveModal(request)}
                                    title="Approve Request"
      className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded p-1"
                                  >
                                    <FaCheck className="w-5 h-5 text-green-600" />
                                  </Button>
                                  <Button
                                                                                  bsPrefix="custom-hamburger"

                                    onClick={() => handleShowRejectModal(request)}
                                    title="Reject Request"
      className="border-purple-300 text-purple-600 hover:bg-purple-50 rounded p-1"
                                  >
                                    <FaTimes className="w-5 h-5 text-red-600" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
              </div>
            </div>
          )}
        </Tab>

        <Tab eventKey="usage" title={
          <span className="d-flex align-items-center gap-4 text-1xl font-bold text-emerald-700 py-2 px-6 backdrop-blur">
            <FaChartBar className="text-emerald-600 w-5 h-5" />
            License Usage
          </span>
        }>
                    <div className="pt-6 md:pt-6"></div>

          {/* Search Control for Usage */}
          <Row className="g-4 mb-6">
            <Col md={6}>
              <InputGroup className="shadow-2xl rounded-xl overflow-hidden border-0 border-purple-300 input-group">
                <InputGroup.Text className="bg-white border-0 text-purple-600 px-6 text-1xl">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by organization name or email..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="border-0 focus:ring-0 focus:outline-none text-xl py-2 form-control"
                />
              </InputGroup>
            </Col>
          </Row>

          {/* License Usage Table */}
          {usageLoading ? (
            <div className="text-center py-32">
              <Spinner animation="border" className="text-purple-600 w-24 h-24" />
              <p className="mt-8 text-1xl font-extrabold text-purple-700">Loading Usage Data...</p>
            </div>
          ) : (
          <div className="overflow-x-auto rounded-3xl shadow-2xl bg-white -mx-4 md:mx-0">
<div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <Table bordered hover className="m-0 min-w-max text-xs sm:text-sm">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="py-2 px-3 font-bold text-left">Organization</th>
                    <th className="py-2 px-3 font-bold text-left">Total Licenses</th>
                    <th className="py-2 px-3 font-bold text-left">Used Licenses</th>
                    <th className="py-2 px-3 font-bold text-left">Remaining Licenses</th>
                    <th className="py-2 px-3 font-bold text-left">Usage %</th>
                    <th className="py-2 px-3 font-bold text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsage.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-32">
                        <Alert variant="info" className="bg-purple-50 border-4 border-purple-300 shadow-2xl mx-20 py-16">
                          <p className="text-5xl font-extrabold text-purple-700 mb-2">No usage data found</p>
                          <p className="text-1xl text-purple-600">{searchTerm && 'Try adjusting your search terms.'}</p>
                        </Alert>
                      </td>
                    </tr>
                  ) : (
                    filteredUsage.map((usage) => {
                      const usagePercentage = usage.total_licenses > 0 
                        ? Math.round((usage.used_licenses / usage.total_licenses) * 100)
                        : 0;
                      
                      const getUsageVariant = (percentage) => {
                        if (percentage >= 90) return 'danger';
                        if (percentage >= 75) return 'warning';
                        return 'success';
                      };

                      return (
                        <tr key={usage.id} className="hover:bg-purple-50 transition-all duration-300">
                          <td className="py-2 px-4 align-middle">
                            <div>
                              <strong className="block text-gray-800">{usage.organization?.name}</strong>
                              {usage.organization?.email && (
                                <div className="text-gray-600 text-sm mt-1">{usage.organization.email}</div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3 text-left align-middle text-1xl font-bold text-purple-700">{usage.total_licenses}</td>
                          <td className="py-2 px-3 text-left align-middle text-1xl font-bold text-red-600">{usage.used_licenses}</td>
                          <td className="py-2 px-3 text-left align-middle text-1xl font-bold text-emerald-600">{usage.remaining_licenses}</td>
                          <td className="py-2 px-3 text-left align-middle">
                            <Badge bg={getUsageVariant(usagePercentage)} className="px-2 py-2 text-1xl font-bold shadow-lg">
                              {usagePercentage}%
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-left align-middle">
                            {usage.remaining_licenses === 0 ? (
                              <Badge bg="danger" className="px-2 py-2 text-1xl font-bold shadow-lg">
                                {/* <FaTimes className="me-3" /> */}
                                Exhausted
                              </Badge>
                            ) : usage.remaining_licenses <= 5 ? (
                              <Badge bg="warning" className="px-2 py-2 text-1xl font-bold shadow-lg">
                                {/* <FaClock className="me-3" /> */}
                                Low
                              </Badge>
                            ) : (
                              <Badge bg="success" className="px-2 py-2 text-1xl font-bold shadow-lg">
                                {/* <FaCheck className="me-3" /> */}
                                Available
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
            </div>
          )}
        </Tab>
      </Tabs>

      {/* Approve Request Modal */}
      <Modal show={showApproveModal} onHide={handleCloseModals} fullscreen="sm-down" backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:py-4 sm:px-4">
          <Modal.Title className="text-1xl font-extrabold">Approve License Request</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-8">
          {selectedRequest && (
            <div>
              <Card className="mb-6 shadow-2xl border-0 rounded-3xl">
                <Card.Body className="p-8 bg-white">
                  <h6 className="text-1xl font-bold text-purple-700 mb-6">Request Details</h6>
                  <p className="mb-2"><strong>Request ID:</strong> #{selectedRequest.id}</p>
                  <p className="mb-2"><strong>Organization:</strong> {selectedRequest.organization?.name}</p>
                  <p className="mb-2"><strong>Requested By:</strong> {selectedRequest.requester?.first_name} {selectedRequest.requester?.last_name}</p>
                  <p className="mb-2"><strong>Licenses Requested:</strong> {selectedRequest.requested_licenses}</p>
                  <p className="mb-2"><strong>Request Date:</strong> {formatDate(selectedRequest.createdAt)}</p>
                  {selectedRequest.notes && (
                    <p className="mb-2"><strong>Request Notes:</strong> {selectedRequest.notes}</p>
                  )}
                </Card.Body>
              </Card>

              <Form className="py-0 px-3">
                <Form.Group className="mb-6">
                  <Form.Label className="text-1xl font-bold text-gray-800">Approved Licenses *</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={approvedLicenses}
                    onChange={(e) => setApprovedLicenses(e.target.value)}
                    isInvalid={!!formErrors.approvedLicenses}
                    placeholder="Enter number of licenses to approve"
                    className="text-1xl py-2 rounded-2xl border-4 border-purple-300 focus:ring-4 focus:ring-purple-400 shadow-xl"
                  />
                  <Form.Control.Feedback type="invalid" className="text-red-600  font-bold">
                    {formErrors.approvedLicenses}
                  </Form.Control.Feedback>
                  <Form.Text className="text-purple-700  font-medium">
                    You can approve a different number than requested
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-6">
                  <Form.Label className="text-1xl font-bold text-gray-800">Admin Notes (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any internal notes about this approval..."
                    className=" rounded-2xl border-4 border-purple-300 focus:ring-4 focus:ring-purple-400 shadow-xl"
                  />
                </Form.Group>
              </Form>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0">
            <Row className="w-100 g-2">
              <Col xs={6}>
          <Button bsPrefix="custom-hamburger" onClick={handleCloseModals} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
            Cancel
          </Button>
            </Col>
          <Col xs={6}>
          <Button 
                bsPrefix="custom-hamburger"
            onClick={handleApproveRequest}
            disabled={modalLoading}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 "
          >
            {modalLoading ? (
              <>
                {/* <Spinner size="lg" className="me-4" /> */}
                Approving...
              </>
            ) : (
              <>
                {/* <FaCheck className="me-4 text-1xl" /> */}
                Approve Request
              </>
            )}
          </Button>
          </Col>
        </Row>
        </Modal.Footer>
      </Modal>

      {/* Reject Request Modal */}
      <Modal show={showRejectModal} onHide={handleCloseModals} fullscreen="sm-down"  backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="text-1xl font-extrabold">Reject License Request</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-8">
          {selectedRequest && (
            <div>
              <Card className="mb-6 shadow-2xl border-0 rounded-3xl">
                <Card.Body className="p-8 bg-white">
                  <h6 className="text-1xl font-bold text-purple-700 mb-6">Request Details</h6>
                  <p className="mb-2"><strong>Request ID:</strong> #{selectedRequest.id}</p>
                  <p className="mb-2"><strong>Organization:</strong> {selectedRequest.organization?.name}</p>
                  <p className="mb-2"><strong>Requested By:</strong> {selectedRequest.requester?.first_name} {selectedRequest.requester?.last_name}</p>
                  <p className="mb-2"><strong>Licenses Requested:</strong> {selectedRequest.requested_licenses}</p>
                  <p className="mb-2"><strong>Request Date:</strong> {formatDate(selectedRequest.createdAt)}</p>
                  {selectedRequest.notes && (
                    <p className="mb-2"><strong>Request Notes:</strong> {selectedRequest.notes}</p>
                  )}
                </Card.Body>
              </Card>

              <Form className="py-0 px-3">
                <Form.Group className="mb-6">
                  <Form.Label className="text-1xl font-bold text-gray-800">Rejection Reason *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    isInvalid={!!formErrors.rejectionReason}
                    placeholder="Please provide a clear reason for rejecting this request..."
                    className=" rounded-2xl border-4 border-purple-300 focus:ring-4 focus:ring-purple-400 shadow-xl"
                  />
                  <Form.Control.Feedback type="invalid" className="text-red-600  font-bold">
                    {formErrors.rejectionReason}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-6">
                  <Form.Label className="text-1xl font-bold text-gray-800">Admin Notes (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any additional internal notes..."
                    className=" rounded-2xl border-4 border-purple-300 focus:ring-4 focus:ring-purple-400 shadow-xl"
                  />
                </Form.Group>
              </Form>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 p-8">
          <Row className="w-100 g-2">
              <Col xs={6}>
          <Button bsPrefix="custom-hamburger" onClick={handleCloseModals} disabled={modalLoading} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
            Cancel
          </Button>
             </Col>
          <Col xs={6}>
          <Button 
            bsPrefix="custom-hamburger"
            onClick={handleRejectRequest}
            disabled={modalLoading}
            className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
          >
            {modalLoading ? (
              <>
                {/* <Spinner size="lg" className="me-4" /> */}
                Rejecting...
              </>
            ) : (
              <>
                {/* <FaTimes className="me-4 text-1xl" /> */}
                Reject Request
              </>
            )}
          </Button>
            </Col>
        </Row>
        </Modal.Footer>
      </Modal>

      {/* Request Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseModals} fullscreen="sm-down" backdrop="static">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="text-1xl font-extrabold">License Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-8">
          {selectedRequest && (
            <Row className="g-6">
              <Col md={6}>
                <Card className="shadow-2xl border-0 rounded-3xl h-100 ">
                  <Card.Header className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-2 rounded-t-3xl">
                    <h6 className="mb-0 text-1xl font-bold">Request Information</h6>
                  </Card.Header>
                  <Card.Body className="p-6">
                    <p className="mb-2"><strong>Request ID:</strong> #{selectedRequest.id}</p>
                    <p className="mb-2"><strong>Licenses Requested:</strong> {selectedRequest.requested_licenses}</p>
                    <p className="mb-2"><strong>Request Date:</strong> {formatDate(selectedRequest.createdAt)}</p>
                    <p className="mb-2"><strong>Status:</strong> 
                      <Badge bg={STATUS_CONFIG[selectedRequest.status].variant} className="ms-3 px-6 py-2 font-bold">
                        {STATUS_CONFIG[selectedRequest.status].label}
                      </Badge>
                    </p>
                    {selectedRequest.notes && (
                      <p className="mb-2"><strong>Request Notes:</strong> {selectedRequest.notes}</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="shadow-2xl border-0 rounded-3xl mb-4">
                  <Card.Header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2 rounded-t-3xl">
                    <h6 className="mb-0 text-1xl font-bold">Organization Details</h6>
                  </Card.Header>
                  <Card.Body className="p-6">
                    <p className="mb-2"><strong>Name:</strong> {selectedRequest.organization?.name}</p>
                    <p className="mb-2"><strong>Email:</strong> {selectedRequest.organization?.email}</p>
                  </Card.Body>
                </Card>

                <Card className="shadow-2xl border-0 rounded-3xl">
                  <Card.Header className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-2 rounded-t-3xl">
                    <h6 className="mb-0 text-1xl font-bold">Requested By</h6>
                  </Card.Header>
                  <Card.Body className="p-6">
                    <p className="mb-2"><strong>Name:</strong> {selectedRequest.requester?.first_name} {selectedRequest.requester?.last_name}</p>
                    <p className="mb-2"><strong>Email:</strong> {selectedRequest.requester?.email}</p>
                  </Card.Body>
                </Card>
              </Col>
              
              {selectedRequest.status !== 'pending' && (
                <Col md={12} className="mt-4">
                  <Card className="shadow-2xl border-0 rounded-3xl">
                    <Card.Header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-2 rounded-t-3xl">
                      <h6 className="mb-0 text-1xl font-bold">Processing Details</h6>
                    </Card.Header>
                    <Card.Body className="p-6">
                      {selectedRequest.status === 'approved' && (
                        <>
                          <p className="mb-2"><strong>Approved Licenses:</strong> {selectedRequest.approved_licenses}</p>
                          <p className="mb-2"><strong>Approved Date:</strong> {formatDate(selectedRequest.approved_at)}</p>
                        </>
                      )}
                      {selectedRequest.status === 'rejected' && (
                        <>
                          <p className="mb-2"><strong>Rejection Reason:</strong> {selectedRequest.rejection_reason}</p>
                          <p className="mb-2"><strong>Rejected Date:</strong> {formatDate(selectedRequest.approved_at)}</p>
                        </>
                      )}
                      {selectedRequest.approver && (
                        <p className="mb-2"><strong>Processed By:</strong> {selectedRequest.approver.first_name} {selectedRequest.approver.last_name}</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 p-8">
          <Button bsPrefix="custom-hamburger" onClick={handleCloseModals} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageLicenses;